import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, ArrowRight, Volume2, Check, X, 
  RotateCcw, Zap, Trophy, ChevronLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Aviation vocabulary for flashcards - EXPANDED CONTENT
const FLASHCARD_SETS = {
  'basic': {
    name: 'Temel Kelimeler',
    cards: [
      { en: 'Aircraft', tr: 'Uçak', example: 'The aircraft is ready for boarding.' },
      { en: 'Cabin', tr: 'Kabin', example: 'Please return to your cabin.' },
      { en: 'Crew', tr: 'Mürettebat', example: 'The crew is here to assist you.' },
      { en: 'Passenger', tr: 'Yolcu', example: 'All passengers must fasten seatbelts.' },
      { en: 'Seat', tr: 'Koltuk', example: 'Please take your seat.' },
      { en: 'Aisle', tr: 'Koridor', example: 'Please keep the aisle clear.' },
      { en: 'Window', tr: 'Pencere', example: 'Would you like a window seat?' },
      { en: 'Emergency', tr: 'Acil Durum', example: 'In case of emergency, stay calm.' },
      { en: 'Seatbelt', tr: 'Emniyet Kemeri', example: 'Please fasten your seatbelt.' },
      { en: 'Oxygen Mask', tr: 'Oksijen Maskesi', example: 'Put on your oxygen mask first.' },
      { en: 'Cockpit', tr: 'Kokpit', example: 'The captain is in the cockpit.' },
      { en: 'Galley', tr: 'Mutfak', example: 'The galley is at the rear of the aircraft.' },
      { en: 'Lavatory', tr: 'Tuvalet', example: 'The lavatory is occupied.' },
      { en: 'Overhead Bin', tr: 'Üst Bagaj Dolabı', example: 'Please store your bag in the overhead bin.' },
      { en: 'Tray Table', tr: 'Tepsi Masası', example: 'Please fold up your tray table.' },
      { en: 'Armrest', tr: 'Kol Dayama', example: 'The armrest can be lifted.' },
      { en: 'Headrest', tr: 'Baş Dayama', example: 'Adjust your headrest for comfort.' },
      { en: 'Row', tr: 'Sıra', example: 'You are in row 15.' },
      { en: 'Exit Row', tr: 'Çıkış Sırası', example: 'Exit row seats have extra legroom.' },
      { en: 'Business Class', tr: 'İş Sınıfı', example: 'Business class is at the front.' },
      { en: 'Economy Class', tr: 'Ekonomi Sınıfı', example: 'Economy class offers great value.' },
      { en: 'First Class', tr: 'Birinci Sınıf', example: 'First class has premium services.' },
      { en: 'Boarding Pass', tr: 'Biniş Kartı', example: 'Please show your boarding pass.' },
      { en: 'Flight Number', tr: 'Uçuş Numarası', example: 'What is your flight number?' },
      { en: 'Destination', tr: 'Varış Noktası', example: 'Our destination is Istanbul.' },
      { en: 'Departure', tr: 'Kalkış', example: 'Departure is at 10:00 AM.' },
      { en: 'Arrival', tr: 'Varış', example: 'Arrival time is 2:00 PM.' },
      { en: 'Connecting Flight', tr: 'Aktarmalı Uçuş', example: 'I have a connecting flight.' },
      { en: 'Check-in', tr: 'Check-in', example: 'Check-in is now open.' },
      { en: 'Baggage Claim', tr: 'Bagaj Alım', example: 'Baggage claim is on Level 1.' }
    ]
  },
  'announcements': {
    name: 'Anons Kelimeleri',
    cards: [
      { en: 'Welcome aboard', tr: 'Uçağımıza hoş geldiniz', example: 'Welcome aboard Flight 123.' },
      { en: 'Departure', tr: 'Kalkış', example: 'Departure is in 10 minutes.' },
      { en: 'Landing', tr: 'İniş', example: 'We are preparing for landing.' },
      { en: 'Turbulence', tr: 'Türbülans', example: 'We may experience some turbulence.' },
      { en: 'Altitude', tr: 'İrtifa', example: 'We are cruising at 35,000 feet altitude.' },
      { en: 'Descent', tr: 'Alçalma', example: 'We have begun our descent.' },
      { en: 'Gate', tr: 'Kapı', example: 'Please proceed to Gate 5.' },
      { en: 'Delay', tr: 'Rötar', example: 'We apologize for the delay.' },
      { en: 'Final approach', tr: 'Son yaklaşma', example: 'We are on final approach.' },
      { en: 'Cruising altitude', tr: 'Seyir irtifası', example: 'We have reached cruising altitude.' },
      { en: 'Fasten seatbelt sign', tr: 'Emniyet kemeri işareti', example: 'The fasten seatbelt sign is on.' },
      { en: 'Local time', tr: 'Yerel saat', example: 'The local time is 3 PM.' },
      { en: 'Weather conditions', tr: 'Hava koşulları', example: 'Weather conditions are favorable.' },
      { en: 'Flight duration', tr: 'Uçuş süresi', example: 'Flight duration is 2 hours.' },
      { en: 'Ground crew', tr: 'Yer ekibi', example: 'Our ground crew is preparing.' },
      { en: 'On behalf of', tr: 'Adına', example: 'On behalf of the captain...' },
      { en: 'Cabin crew', tr: 'Kabin ekibi', example: 'Cabin crew, prepare for takeoff.' },
      { en: 'Cross-check', tr: 'Çapraz kontrol', example: 'Doors to automatic and cross-check.' },
      { en: 'Taxiing', tr: 'Taksi yapma', example: 'We are now taxiing to the runway.' },
      { en: 'Runway', tr: 'Pist', example: 'We are cleared for runway 27.' },
      { en: 'Takeoff', tr: 'Kalkış', example: 'Prepare for takeoff.' },
      { en: 'In-flight service', tr: 'Uçak içi servis', example: 'In-flight service will begin shortly.' },
      { en: 'Duty-free', tr: 'Gümrüksüz satış', example: 'Duty-free items are available.' },
      { en: 'Complimentary', tr: 'Ücretsiz', example: 'This is a complimentary service.' },
      { en: 'Approximate', tr: 'Yaklaşık', example: 'Approximate landing time is 5 PM.' }
    ]
  },
  'icao': {
    name: 'ICAO Alfabesi',
    cards: [
      { en: 'Alpha', tr: 'A harfi', example: 'A as in Alpha' },
      { en: 'Bravo', tr: 'B harfi', example: 'B as in Bravo' },
      { en: 'Charlie', tr: 'C harfi', example: 'C as in Charlie' },
      { en: 'Delta', tr: 'D harfi', example: 'D as in Delta' },
      { en: 'Echo', tr: 'E harfi', example: 'E as in Echo' },
      { en: 'Foxtrot', tr: 'F harfi', example: 'F as in Foxtrot' },
      { en: 'Golf', tr: 'G harfi', example: 'G as in Golf' },
      { en: 'Hotel', tr: 'H harfi', example: 'H as in Hotel' },
      { en: 'India', tr: 'I harfi', example: 'I as in India' },
      { en: 'Juliet', tr: 'J harfi', example: 'J as in Juliet' },
      { en: 'Kilo', tr: 'K harfi', example: 'K as in Kilo' },
      { en: 'Lima', tr: 'L harfi', example: 'L as in Lima' },
      { en: 'Mike', tr: 'M harfi', example: 'M as in Mike' },
      { en: 'November', tr: 'N harfi', example: 'N as in November' },
      { en: 'Oscar', tr: 'O harfi', example: 'O as in Oscar' },
      { en: 'Papa', tr: 'P harfi', example: 'P as in Papa' },
      { en: 'Quebec', tr: 'Q harfi', example: 'Q as in Quebec' },
      { en: 'Romeo', tr: 'R harfi', example: 'R as in Romeo' },
      { en: 'Sierra', tr: 'S harfi', example: 'S as in Sierra' },
      { en: 'Tango', tr: 'T harfi', example: 'T as in Tango' },
      { en: 'Uniform', tr: 'U harfi', example: 'U as in Uniform' },
      { en: 'Victor', tr: 'V harfi', example: 'V as in Victor' },
      { en: 'Whiskey', tr: 'W harfi', example: 'W as in Whiskey' },
      { en: 'X-ray', tr: 'X harfi', example: 'X as in X-ray' },
      { en: 'Yankee', tr: 'Y harfi', example: 'Y as in Yankee' },
      { en: 'Zulu', tr: 'Z harfi', example: 'Z as in Zulu' }
    ]
  },
  'safety': {
    name: 'Güvenlik Kelimeleri',
    cards: [
      { en: 'Life vest', tr: 'Can yeleği', example: 'Your life vest is under your seat.' },
      { en: 'Emergency exit', tr: 'Acil çıkış', example: 'Locate your nearest emergency exit.' },
      { en: 'Evacuation', tr: 'Tahliye', example: 'Please follow evacuation procedures.' },
      { en: 'Brace position', tr: 'Koruma pozisyonu', example: 'Assume the brace position.' },
      { en: 'Fire extinguisher', tr: 'Yangın söndürücü', example: 'Fire extinguishers are located here.' },
      { en: 'First aid', tr: 'İlk yardım', example: 'We have first aid kits on board.' },
      { en: 'Safety demonstration', tr: 'Güvenlik gösterisi', example: 'Please watch the safety demonstration.' },
      { en: 'Emergency slide', tr: 'Acil kaydırak', example: 'The emergency slide will inflate automatically.' },
      { en: 'Flotation device', tr: 'Yüzme yardımcısı', example: 'Your seat cushion is a flotation device.' },
      { en: 'Smoke detector', tr: 'Duman dedektörü', example: 'Smoke detectors are in the lavatories.' },
      { en: 'Decompression', tr: 'Basınç düşmesi', example: 'In case of decompression, masks will drop.' },
      { en: 'Ditching', tr: 'Suya iniş', example: 'Ditching procedures are in your safety card.' },
      { en: 'Mayday', tr: 'İmdat çağrısı', example: 'Mayday is an emergency distress call.' },
      { en: 'Pan-pan', tr: 'Acil durum çağrısı', example: 'Pan-pan indicates an urgency situation.' },
      { en: 'Extinguish', tr: 'Söndürmek', example: 'Please extinguish all smoking materials.' },
      { en: 'Comply', tr: 'Uymak', example: 'Please comply with crew instructions.' },
      { en: 'Evacuate', tr: 'Tahliye etmek', example: 'We need to evacuate immediately.' },
      { en: 'Medical emergency', tr: 'Tıbbi acil durum', example: 'We have a medical emergency on board.' },
      { en: 'AED', tr: 'Otomatik Defibrilatör', example: 'The AED is located in the galley.' },
      { en: 'CPR', tr: 'Kalp masajı', example: 'Do you know how to perform CPR?' },
      { en: 'Unconscious', tr: 'Bilinçsiz', example: 'The passenger is unconscious.' },
      { en: 'Oxygen', tr: 'Oksijen', example: 'Supplemental oxygen is available.' },
      { en: 'Turbulence injury', tr: 'Türbülans yaralanması', example: 'Wear seatbelt to avoid turbulence injury.' },
      { en: 'Secure', tr: 'Güvenceye almak', example: 'Please secure all loose items.' },
      { en: 'Prohibited items', tr: 'Yasak maddeler', example: 'Prohibited items are listed in the safety card.' }
    ]
  },
  'service': {
    name: 'Servis Kelimeleri',
    cards: [
      { en: 'Beverage', tr: 'İçecek', example: 'Would you like a beverage?' },
      { en: 'Meal', tr: 'Yemek', example: 'Chicken or beef for your meal?' },
      { en: 'Snack', tr: 'Atıştırmalık', example: 'We have snacks available.' },
      { en: 'Refreshment', tr: 'Serinletici içecek', example: 'Refreshments will be served shortly.' },
      { en: 'Vegetarian', tr: 'Vejetaryen', example: 'Do you have a vegetarian option?' },
      { en: 'Special meal', tr: 'Özel yemek', example: 'Did you order a special meal?' },
      { en: 'Allergies', tr: 'Alerjiler', example: 'Please inform us of any allergies.' },
      { en: 'Gluten-free', tr: 'Glutensiz', example: 'We have gluten-free options.' },
      { en: 'Halal', tr: 'Helal', example: 'This meal is halal certified.' },
      { en: 'Kosher', tr: 'Koşer', example: 'Kosher meals are available on request.' },
      { en: 'Hot towel', tr: 'Sıcak havlu', example: 'Would you like a hot towel?' },
      { en: 'Blanket', tr: 'Battaniye', example: 'May I get you a blanket?' },
      { en: 'Pillow', tr: 'Yastık', example: 'Here is a pillow for you.' },
      { en: 'Headphones', tr: 'Kulaklık', example: 'Headphones are complimentary.' },
      { en: 'Amenity kit', tr: 'Konfor seti', example: 'Your amenity kit includes a toothbrush.' },
      { en: 'Entertainment', tr: 'Eğlence', example: 'In-flight entertainment is available.' },
      { en: 'Wi-Fi', tr: 'Kablosuz internet', example: 'Wi-Fi is available for purchase.' },
      { en: 'Magazine', tr: 'Dergi', example: 'Would you like a magazine?' },
      { en: 'Newspaper', tr: 'Gazete', example: 'We have today\'s newspaper.' },
      { en: 'Duty-free', tr: 'Gümrüksüz', example: 'Browse our duty-free catalog.' }
    ]
  },
  'passenger_types': {
    name: 'Yolcu Tipleri',
    cards: [
      { en: 'Unaccompanied minor', tr: 'Refakatsiz çocuk', example: 'We have an unaccompanied minor on board.' },
      { en: 'Wheelchair passenger', tr: 'Tekerlekli sandalye yolcusu', example: 'A wheelchair passenger needs assistance.' },
      { en: 'Pregnant passenger', tr: 'Hamile yolcu', example: 'The pregnant passenger needs extra care.' },
      { en: 'Elderly passenger', tr: 'Yaşlı yolcu', example: 'Please assist the elderly passenger.' },
      { en: 'VIP', tr: 'Önemli kişi', example: 'We have a VIP in first class.' },
      { en: 'Frequent flyer', tr: 'Sık uçan yolcu', example: 'This frequent flyer has elite status.' },
      { en: 'First-time flyer', tr: 'İlk kez uçan', example: 'Be patient with first-time flyers.' },
      { en: 'Nervous flyer', tr: 'Uçuştan korkan', example: 'Reassure the nervous flyer.' },
      { en: 'Business traveler', tr: 'İş seyahati yapan', example: 'Business travelers need quick service.' },
      { en: 'Family with children', tr: 'Çocuklu aile', example: 'Help the family with children first.' },
      { en: 'Transit passenger', tr: 'Transit yolcu', example: 'Transit passengers stay on board.' },
      { en: 'Deportee', tr: 'Sınır dışı edilen', example: 'A deportee requires special handling.' },
      { en: 'Medical case', tr: 'Tıbbi vaka', example: 'We have a medical case in row 20.' },
      { en: 'Pet owner', tr: 'Evcil hayvan sahibi', example: 'The pet owner has a cat in the cabin.' },
      { en: 'Service animal', tr: 'Hizmet hayvanı', example: 'Service animals are allowed in the cabin.' }
    ]
  }
};

export default function CabinCrewFlashcards() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedSet, setSelectedSet] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [known, setKnown] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);

  const currentCards = selectedSet ? FLASHCARD_SETS[selectedSet].cards : [];
  const currentCard = currentCards[currentIndex];

  const playAudio = async (text) => {
    // First try browser TTS as primary (OpenAI rate limited)
    if ('speechSynthesis' in window) {
      try {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        speechSynthesis.speak(utterance);
        return;
      } catch (e) {
        console.error('Browser TTS failed:', e);
      }
    }
    
    // Fallback to API if browser TTS not available
    try {
      const response = await axios.post(
        `${API_URL}/api/speaking/text-to-speech`,
        { text },
        { responseType: 'blob' }
      );
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('audio')) {
        const audio = new Audio(URL.createObjectURL(response.data));
        audio.play();
      }
    } catch (error) {
      console.error('Audio API error:', error);
    }
  };

  const handleSwipe = (direction) => {
    setSwipeDirection(direction);
    
    setTimeout(() => {
      if (direction === 'right') {
        setKnown([...known, currentCard]);
        toast.success('Biliyorum! ✓');
      } else {
        setUnknown([...unknown, currentCard]);
        toast.error('Tekrar et');
      }
      
      if (currentIndex < currentCards.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setIsFlipped(false);
      } else {
        setCompleted(true);
      }
      setSwipeDirection(null);
    }, 300);
  };

  const resetDeck = () => {
    setCurrentIndex(0);
    setKnown([]);
    setUnknown([]);
    setIsFlipped(false);
    setCompleted(false);
  };

  const practiceUnknown = () => {
    if (unknown.length > 0) {
      // Reset with only unknown cards
      const unknownSet = { ...FLASHCARD_SETS[selectedSet], cards: unknown };
      FLASHCARD_SETS['review'] = unknownSet;
      setSelectedSet('review');
      setCurrentIndex(0);
      setKnown([]);
      setUnknown([]);
      setIsFlipped(false);
      setCompleted(false);
    }
  };

  // Set selection screen
  if (!selectedSet) {
    return (
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 pb-8 rounded-b-[2rem]">
            <div className="max-w-4xl mx-auto px-4 pt-4">
              <button
                onClick={() => navigate('/english-training')}
                className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Geri</span>
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                  <Zap className="w-8 h-8 text-amber-500" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Flashcards</h1>
                  <p className="text-amber-100 text-sm">Swipe ile hızlı kelime öğren</p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
            {/* Set Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(FLASHCARD_SETS).map(([key, set]) => (
                <Card 
                  key={key}
                  className="cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1 border-0 shadow-lg hover:border-amber-300"
                  onClick={() => setSelectedSet(key)}
                >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <Badge className="bg-amber-500 text-white">
                      {set.cards.length} Kart
                    </Badge>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{set.name}</h3>
                  <p className="text-sm text-gray-500">
                    {set.cards.slice(0, 3).map(c => c.en).join(', ')}...
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      
    );
  }

  // Completed screen
  if (completed) {
    const successRate = Math.round((known.length / currentCards.length) * 100);
    
    return (
      
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4 flex items-center justify-center">
          <Card className="max-w-md w-full border-4 border-amber-300 shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-3xl font-black text-gray-800 mb-4">
                Tamamlandı! 🎉
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-100 rounded-xl p-4">
                  <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-3xl font-black text-green-600">{known.length}</p>
                  <p className="text-sm text-gray-600">Biliyorum</p>
                </div>
              <div className="bg-red-100 rounded-xl p-4">
                <X className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-3xl font-black text-red-600">{unknown.length}</p>
                <p className="text-sm text-gray-600">Tekrar Et</p>
              </div>
            </div>
            
            <div className="bg-amber-100 rounded-xl p-4 mb-6">
              <p className="text-4xl font-black text-amber-600">%{successRate}</p>
              <p className="text-sm text-gray-600">Başarı Oranı</p>
            </div>
            
            <div className="space-y-3">
              {unknown.length > 0 && (
                <Button
                  onClick={practiceUnknown}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Bilmediklerimi Tekrar Et ({unknown.length})
                </Button>
              )}
              <Button
                onClick={resetDeck}
                variant="outline"
                className="w-full"
              >
                Baştan Başla
              </Button>
              <Button
                onClick={() => {
                  setSelectedSet(null);
                  setCurrentIndex(0);
                  setKnown([]);
                  setUnknown([]);
                  setIsFlipped(false);
                  setCompleted(false);
                }}
                variant="ghost"
                className="w-full"
              >
                Farklı Set Seç
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
    );
  }

  // Flashcard practice screen
  return (
    
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4">
        <div className="container mx-auto max-w-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedSet(null);
                setCurrentIndex(0);
                setKnown([]);
                setUnknown([]);
                setIsFlipped(false);
                setCompleted(false);
              }}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <p className="text-sm text-gray-500">{FLASHCARD_SETS[selectedSet]?.name}</p>
              <p className="font-bold text-gray-800">
                {currentIndex + 1} / {currentCards.length}
              </p>
            </div>
            <div className="flex gap-2">
              <Badge className="bg-green-500 text-white">{known.length}</Badge>
              <Badge className="bg-red-500 text-white">{unknown.length}</Badge>
            </div>
          </div>

          {/* Progress bar */}
        <div className="h-2 bg-gray-200 rounded-full mb-6 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
            style={{ width: `${((currentIndex) / currentCards.length) * 100}%` }}
          />
        </div>

        {/* Flashcard */}
        <div 
          className={`relative h-80 perspective-1000 mb-6 transition-transform duration-300 ${
            swipeDirection === 'left' ? '-translate-x-full rotate-12 opacity-0' :
            swipeDirection === 'right' ? 'translate-x-full -rotate-12 opacity-0' : ''
          }`}
        >
          <div
            className={`w-full h-full cursor-pointer transition-transform duration-500 ${
              isFlipped ? '[transform:rotateY(180deg)]' : ''
            }`}
            onClick={() => setIsFlipped(!isFlipped)}
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Front */}
            <Card 
              className="absolute w-full h-full border-4 border-amber-300 shadow-xl"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <CardContent className="h-full flex flex-col items-center justify-center p-6">
                <button
                  onClick={(e) => { e.stopPropagation(); playAudio(currentCard?.en); }}
                  className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-4 hover:scale-110 transition-transform"
                >
                  <Volume2 className="w-8 h-8 text-white" />
                </button>
                <p className="text-4xl font-black text-gray-800 mb-4 text-center">
                  {currentCard?.en}
                </p>
                <p className="text-sm text-gray-400">Kartı çevirmek için tıkla</p>
              </CardContent>
            </Card>

            {/* Back */}
            <Card 
              className="absolute w-full h-full border-4 border-sky-300 shadow-xl bg-gradient-to-br from-sky-50 to-blue-50"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <CardContent className="h-full flex flex-col items-center justify-center p-6">
                <p className="text-3xl font-black text-sky-700 mb-4 text-center">
                  {currentCard?.tr}
                </p>
                <div className="bg-white/80 rounded-xl p-4 w-full">
                  <p className="text-sm text-gray-500 mb-1">Örnek:</p>
                  <p className="text-gray-700 italic">{currentCard?.example}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Swipe buttons */}
        <div className="flex justify-center gap-8">
          <button
            onClick={() => handleSwipe('left')}
            className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <X className="w-10 h-10 text-white" />
          </button>
          
          <button
            onClick={() => setIsFlipped(!isFlipped)}
            className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <RotateCcw className="w-8 h-8 text-white" />
          </button>
          
          <button
            onClick={() => handleSwipe('right')}
            className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <Check className="w-10 h-10 text-white" />
          </button>
        </div>

        {/* Instructions */}
        <div className="text-center mt-6 text-gray-500 text-sm">
          <p>← Bilmiyorum | Çevir | Biliyorum →</p>
        </div>
      </div>
    </div>
    
  );
}
